import { dec, formatQuantity, plural, withGrownForm, ZERO } from "@bl/domain";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  Archive,
  ArrowLeft,
  CalendarPlus,
  Check,
  CopyPlus,
  Ellipsis,
  FileClock,
  Flame,
  History,
  Link2,
  PackagePlus,
  Pencil,
  Printer,
  Search,
  Star,
} from "lucide-react";
import { type CSSProperties, useState } from "react";
import {
  type RecipeDetail,
  type RecipeLine,
  send,
  useChange,
  useRecipe,
  useRecipeDrafts,
} from "../api";
import { BeerGlass } from "../components/BeerGlass";
import { useMedia } from "../components/DocumentViewer";
import { PrintSheets } from "../components/PrintSheets";
import { inRange, StyleBar } from "../components/StyleBar";
import {
  Dialog,
  ErrorNote,
  Menu,
  MenuDivider,
  MenuHeading,
  MenuItem,
  Section,
  Toolbar,
  ToolbarButton,
  ToolbarSeparator,
  usePageTitle,
} from "../components/ui";
import { showDate, showDateTime } from "../format";
import { useNotify } from "../notify";
import {
  brewingFacts,
  ingredientGroups,
  lineFacts,
  lineName,
  lineOrigin,
  RECIPE_TYPE_LABELS,
  showBarrels,
  showFahrenheit,
  showGallons,
  showGravity,
  showShortBy,
  srmColor,
} from "../recipeFormat";
import { shortVerdict, useShortFrom } from "../shortFrom";
import { LinkPicker } from "./IngredientPicker";
import { PlanBrewDialog } from "./Schedule";

/**
 * Recipes (PLAN.md, "Slice 2"): a card for each, after Profilarr's quality profiles, and a page for one.
 * Every figure is an estimate worked out from the recipe as written; a brew will keep what it measured.
 */

export const beer = (srm: number) => ({ "--beer": srmColor(srm) }) as CSSProperties;
export const showAbv = (abv: number | null) => (abv === null ? "-" : `${abv.toFixed(1)}%`);
export const styleLabel = (s: { name: string; code: string | null }) =>
  s.code ? `${s.name} ${s.code}` : s.name;
/** A recipe's star, shown as pressed while the change is on its way. */
export function useStar(r: { id: string; favorite: boolean }) {
  const star = useChange((favorite: boolean, key) =>
    send.favoriteRecipe({ id: r.id, favorite }, key),
  );
  return {
    on: star.isPending ? !r.favorite : r.favorite,
    pending: star.isPending,
    toggle: () => star.mutate(!r.favorite),
  };
}
/** The star in a recipe page's toolbar. */
function StarButton({ recipe: r }: { recipe: { id: string; favorite: boolean } }) {
  const { on, pending, toggle } = useStar(r);
  return (
    <ToolbarButton
      icon={<Star size={20} className={on ? "star-on" : undefined} />}
      label={on ? "Starred" : "Star"}
      title={on ? "Take the star off" : "Star this recipe, to keep it at the top of the list"}
      aria-pressed={on}
      disabled={pending}
      onClick={toggle}
    />
  );
}
/** The star, as a phone's More menu has it: checked when the recipe is starred. */
function StarMenuItem({ recipe: r }: { recipe: { id: string; favorite: boolean } }) {
  const { on, pending, toggle } = useStar(r);
  return (
    <MenuItem checked={on} disabled={pending} onClick={toggle}>
      Starred
    </MenuItem>
  );
}
export function RecipePage() {
  const { recipeId } = useParams({ from: "/recipes/$recipeId" });
  const { data: recipe, isLoading, error } = useRecipe(recipeId);
  usePageTitle(recipe?.name ?? "Recipe", "Recipes");
  const [archiving, setArchiving] = useState(false);
  const navigate = useNavigate();
  const brewing = useChange((_: undefined, key) => send.startBrew(recipeId, key), {
    onSuccess: (brew) => navigate({ to: "/brews/$brewId", params: { brewId: brew.id } }),
  });
  const [scheduling, setScheduling] = useState(false);
  const phone = useMedia("(max-width: 760px)");

  if (isLoading) return <div className="page-body muted">Loading</div>;
  if (error || !recipe) {
    return (
      <div className="page-body">
        <ErrorNote error={error ?? new Error("Recipe not found")} />
      </div>
    );
  }
  return (
    <>
      {/* Making things on the left, as on every page: a brew, a planned brew, a copy (the Same Place
          Rule, DESIGN.md; the re-critique, 2026-10-06, found them on the right). */}
      <Toolbar
        left={
          <>
            <Link to="/recipes" className="toolbar-button" title="Back to recipes">
              <ArrowLeft size={20} />
              <span className="label">Recipes</span>
            </Link>
            <ToolbarSeparator />
            <ToolbarButton
              icon={<Flame size={20} />}
              label="Brew this"
              labeled
              title="Plan a brew of this version: what to pull, from which lots"
              disabled={brewing.isPending}
              onClick={() => brewing.mutate(undefined)}
            />
            <ToolbarButton
              icon={<CalendarPlus size={20} />}
              label="Schedule"
              labeled
              title="Put a brew of this recipe on the calendar for a day"
              onClick={() => setScheduling(true)}
            />
            {/* Copy starts a new recipe from this one (Tyler, 2026-10-02), written only on Save. It took
                New version's place: Edit's Save as already offers the next version. */}
            {phone ? null : (
              <Link
                to="/recipes/builder"
                search={{ from: recipe.id }}
                className="toolbar-button"
                title="Start a new recipe copied from this one"
              >
                <CopyPlus size={20} />
                <span className="label">Copy</span>
              </Link>
            )}
          </>
        }
        right={
          phone ? (
            // On a phone the rest share one button, so Brew this keeps its words (the re-critique,
            // 2026-10-07: a recipe's toolbar was seven bare glyphs).
            <Menu icon={<Ellipsis size={20} />} label="More" labeled>
              {(close) => (
                <>
                  <StarMenuItem recipe={recipe} />
                  <MenuItem
                    onClick={() => {
                      close();
                      navigate({ to: "/recipes/builder", search: { from: recipe.id } });
                    }}
                  >
                    Copy to a new recipe
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      close();
                      navigate({ to: "/recipes/$recipeId/edit", params: { recipeId: recipe.id } });
                    }}
                  >
                    Edit
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      close();
                      window.print();
                    }}
                  >
                    Print
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      close();
                      setArchiving(true);
                    }}
                  >
                    Put away
                  </MenuItem>
                  {recipe.versions.length > 1 ? (
                    <>
                      <MenuDivider />
                      <VersionItems recipe={recipe} close={close} />
                    </>
                  ) : null}
                </>
              )}
            </Menu>
          ) : (
            <>
              <StarButton recipe={recipe} />
              <VersionsMenu recipe={recipe} />
              {/* Its Brew Log and recipe sheet for the tank's clipboard (Scott's Jam, 2026-10-08). */}
              <ToolbarButton
                icon={<Printer size={20} />}
                label="Print"
                title="Print its Brew Log and recipe sheet"
                onClick={() => window.print()}
              />
              <Link
                to="/recipes/$recipeId/edit"
                params={{ recipeId: recipe.id }}
                className="toolbar-button"
                title="Edit this version"
              >
                <Pencil size={20} />
                <span className="label">Edit</span>
              </Link>
              <ToolbarButton
                icon={<Archive size={20} />}
                label="Put away"
                onClick={() => setArchiving(true)}
              />
            </>
          )
        }
      />
      <div className="page-body">
        <RecipeHero recipe={recipe} />
        <WaitingChanges recipe={recipe} />
        <ErrorNote error={brewing.error} />
        <Gauges recipe={recipe} />
        <IngredientsSection recipe={recipe} />
        <BrewingSection recipe={recipe} />
        <HistorySection recipe={recipe} />
        {recipe.notes || recipe.tasteNotes ? (
          <Section id="recipe-notes" title="Notes">
            {recipe.notes ? <p style={{ whiteSpace: "pre-wrap" }}>{recipe.notes}</p> : null}
            {recipe.tasteNotes ? (
              <p style={{ whiteSpace: "pre-wrap" }}>
                <strong>Tasting:</strong> {recipe.tasteNotes}
              </p>
            ) : null}
          </Section>
        ) : null}
      </div>
      <PrintSheets recipe={recipe} />
      <ArchiveDialog recipe={recipe} open={archiving} onClose={() => setArchiving(false)} />
      <PlanBrewDialog open={scheduling} onClose={() => setScheduling(false)} recipeId={recipe.id} />
    </>
  );
}
/**
 * The recipe's other versions, from a button in the toolbar (Tyler, 2026-10-03) rather than a row of
 * links under the title. Shown once there is more than one.
 */
function VersionsMenu({ recipe: r }: { recipe: RecipeDetail }) {
  if (r.versions.length < 2) return null;
  return (
    <Menu icon={<History size={20} />} label="Version history">
      {(close) => <VersionItems recipe={r} close={close} />}
    </Menu>
  );
}
/** A recipe's versions, each opening its own page, in Version history or a phone's More. */
function VersionItems({ recipe: r, close }: { recipe: RecipeDetail; close: () => void }) {
  const navigate = useNavigate();
  return (
    <>
      <MenuHeading>Versions</MenuHeading>
      {r.versions.map((v) => (
        <MenuItem
          key={v.id}
          checked={v.id === r.id}
          onClick={() => {
            close();
            if (v.id !== r.id) navigate({ to: "/recipes/$recipeId", params: { recipeId: v.id } });
          }}
        >
          Version {v.version}
          <span className="faint version-saved">Saved {showDate(v.updatedAt)}</span>
        </MenuItem>
      ))}
    </>
  );
}
function RecipeHero({ recipe: r }: { recipe: RecipeDetail }) {
  return (
    <div className="item-hero recipe-hero" style={beer(r.estimates.srm)}>
      <div>
        <div className="recipe-pills">
          {r.style ? (
            <span className="chip plain">
              {styleLabel(r.style)}
              {r.style.guide ? `, ${r.style.guide}` : ""}
            </span>
          ) : null}
          <span className="chip plain">{RECIPE_TYPE_LABELS[r.type] ?? r.type}</span>
          <span className="chip plain">Version {r.version}</span>
        </div>
        <h1>{r.name}</h1>
        <div className="meta">
          {r.brewer ? <span>By {r.brewer}</span> : null}
          <span>
            {showGallons(r.batchLiters)} ({showBarrels(r.batchLiters)})
          </span>
          {r.writtenOn ? <span>Written {showDate(r.writtenOn)}</span> : null}
          {r.importedFrom ? <span>Imported from {r.importedFrom}</span> : null}
        </div>
        {r.description ? <p className="muted">{r.description}</p> : null}
      </div>
      <BeerGlass style={r.style?.name} title={`About ${r.estimates.srm.toFixed(1)} SRM`} />
    </div>
  );
}
/**
 * Each estimate against its style's range, with what BeerSmith said beside it where it said. A style
 * with no ranges at all (one typed in, not picked from the guide) draws no bars, as in the builder.
 */
function Gauges({ recipe: r }: { recipe: RecipeDetail }) {
  const e = r.estimates;
  const bars = (["og", "fg", "abv", "ibu", "srm"] as const).some((f) => r.style?.[f]);
  // A figure outside its style's range, in the warning's color as its gauge shows it.
  const off = (f: "og" | "fg" | "abv" | "ibu" | "srm", value: number | null) => {
    const range = r.style?.[f];
    return range && value !== null && !inRange(range, value) ? "out-of-range" : undefined;
  };
  return (
    <>
      {/* On a phone, the five figures in one line, so the ingredients and their stock come next on
          the first screen (the re-critique, 2026-10-07: five tiles filled it). */}
      <p className="gauge-line num">
        <span className={off("og", e.og)}>{showGravity(e.og)}</span> →{" "}
        <span className={off("fg", e.fg)}>{showGravity(e.fg)}</span> ·{" "}
        <span className={off("abv", e.abv)}>{showAbv(e.abv)}</span> ·{" "}
        <span className={off("ibu", e.ibu)}>{e.ibu.toFixed(0)} IBU</span> ·{" "}
        <span className={off("srm", e.srm)}>{e.srm.toFixed(0)} SRM</span>
      </p>
      <GaugeTiles recipe={r} bars={bars} />
    </>
  );
}
function GaugeTiles({ recipe: r, bars }: { recipe: RecipeDetail; bars: boolean }) {
  const e = r.estimates;
  const said = r.reported;
  return (
    <div className="gauges">
      <Gauge
        bar={bars}
        label="Original gravity"
        value={e.og}
        text={showGravity(e.og)}
        range={r.style?.og ?? null}
        reported={said?.og != null ? showGravity(said.og) : null}
      />
      <Gauge
        bar={bars}
        label="Final gravity"
        value={e.fg}
        text={showGravity(e.fg)}
        range={r.style?.fg ?? null}
        reported={said?.fg != null ? showGravity(said.fg) : null}
      />
      <Gauge
        bar={bars}
        label="ABV"
        value={e.abv}
        text={showAbv(e.abv)}
        range={r.style?.abv ?? null}
        reported={said?.abv != null ? showAbv(said.abv) : null}
      />
      <Gauge
        bar={bars}
        label="Bitterness"
        value={e.ibu}
        text={`${e.ibu.toFixed(1)} IBU`}
        range={r.style?.ibu ?? null}
        reported={said?.ibu != null ? `${said.ibu.toFixed(1)} IBU` : null}
      />
      <Gauge
        bar={bars}
        label="Color"
        value={e.srm}
        text={`${e.srm.toFixed(1)} SRM`}
        range={r.style?.srm ?? null}
        reported={said?.srm != null ? `${said.srm.toFixed(1)} SRM` : null}
      />
    </div>
  );
}
function Gauge({
  bar,
  label,
  value,
  text,
  range,
  reported,
}: {
  bar: boolean;
  label: string;
  value: number | null;
  text: string;
  range: { min: number; max: number } | null;
  reported: string | null;
}) {
  const off = range && value !== null ? !inRange(range, value) : false;
  return (
    <div className="gauge">
      <div className="label">{label}</div>
      <div className={`value num ${off ? "out-of-range" : ""}`}>{text}</div>
      {bar ? (
        <StyleBar
          range={range}
          value={value}
          title={range ? `Style: ${range.min} to ${range.max}` : "No style range"}
        />
      ) : null}
      <div className="faint reported">{reported ? `BeerSmith: ${reported}` : " "}</div>
    </div>
  );
}
function IngredientsSection({ recipe: r }: { recipe: RecipeDetail }) {
  const groups = ingredientGroups(r.lines);
  const linked = r.lines.filter((l) => l.item).length;
  // An item on several lines is checked once for all of them, so its row says how many share it.
  const linesOf = new Map<string, number>();
  for (const l of r.lines) if (l.item) linesOf.set(l.item.id, (linesOf.get(l.item.id) ?? 0) + 1);
  return (
    <Section
      id="recipe-ingredients"
      title="Ingredients"
      aside={`${r.lines.length} lines, ${linked ? `${linked} linked to inventory` : "none linked to inventory yet"}`}
    >
      <div className="table-wrap">
        <table className="table recipe-lines">
          <thead>
            <tr>
              <th className="num">Amount</th>
              <th>Ingredient</th>
              <th>Details</th>
              <th className="num">Share</th>
              <th>Stock</th>
            </tr>
          </thead>
          {groups.map((g) => (
            <tbody key={g.title}>
              <tr className="group-row">
                <th colSpan={5}>{g.title}</th>
              </tr>
              {g.lines.map((l) => (
                <IngredientRow
                  key={l.id}
                  recipeId={r.id}
                  line={l}
                  sharing={l.item ? (linesOf.get(l.item.id) ?? 1) : 1}
                />
              ))}
            </tbody>
          ))}
        </table>
      </div>
    </Section>
  );
}
function IngredientRow({
  recipeId,
  line: l,
  sharing,
}: {
  recipeId: string;
  line: RecipeLine;
  /** How many of the recipe's lines are made with this line's item, this one included. */
  sharing: number;
}) {
  const { amount, details, share } = lineFacts(l);
  const origin = lineOrigin(l);
  return (
    <tr>
      <td className="num">{amount}</td>
      <td>
        <div>
          {/* A linked line goes by its item, and opens it (Scott's Jam, 2026-10-08). */}
          {l.item ? (
            <Link to="/items/$itemId" params={{ itemId: l.item.id }}>
              {lineName(l)}
            </Link>
          ) : (
            l.name
          )}
          {origin ? <span className="faint"> ({origin})</span> : null}
        </div>
        <LineLink recipeId={recipeId} line={l} />
      </td>
      <td className="muted">{details}</td>
      <td className="num">
        {/* A hop's bitterness, with what BeerSmith made it where the file said. */}
        <span
          title={
            l.kind === "hop" && l.reportedIbu !== null
              ? `BeerSmith: ${l.reportedIbu.toFixed(1)} IBU`
              : undefined
          }
        >
          {share}
        </span>
      </td>
      <td>
        <LineStock line={l} sharing={sharing} />
      </td>
    </tr>
  );
}
/**
 * Whether what is on hand covers a linked line, and if not, by how much: Scott could see that the malt
 * was short but not how far, nor what was on hand (Jam, 2026-10-02). It is written out rather than
 * left to a hover, which a phone does not have. Amounts are the item's, in its unit, as Inventory
 * shows them; an item on several lines is short once, for all of them together.
 */
function LineStock({ line: l, sharing }: { line: RecipeLine; sharing: number }) {
  const shortFrom = useShortFrom();
  if (!l.item || !l.onHand) return <span className="faint">-</span>;
  const s = l.onHand;
  const unit = l.item.displayUnit;
  const q = (canonical: string) => formatQuantity(canonical, { unit });
  // Short counts the brews already planned, so it says so: the recipe brewed after them.
  const says = [`${q(s.quantity)} on hand`];
  if (dec(s.planned).gt(ZERO)) says.push(`${q(s.planned)} already planned`);
  if (sharing > 1 && s.needed !== null) says.push(`${q(s.needed)} for ${sharing} lines`);
  // When it bites, as Inventory says it (the re-critique, 2026-10-06: the recipe said "Short" in red
  // while To order said all clear). Short of what is on hand now is red; short only once the planned
  // brews take theirs is red inside To order's window and a warning beyond it.
  const now = !s.enough && s.needed !== null && dec(s.quantity).lt(dec(s.needed));
  const { tone, when } = shortVerdict(s.enough, now, s.enough || now ? null : shortFrom(l.item.id));
  return (
    <div className="line-stock">
      <span className={`chip ${tone}`}>
        {s.enough ? "On hand" : s.short === null ? "Short" : `Short ${showShortBy(s.short, unit)}`}
      </span>
      {when ? <div className={`line-stock-when ${tone}`}>{when}</div> : null}
      <div className="faint">{says.join(", ")}</div>
    </div>
  );
}
/**
 * Which of the brewery's items a line is made with, matched as an invoice's lines are: a suggestion
 * the brewer presses, or any item found by searching, the catalog's and new ones included. The line
 * keeps its own figures either way; the item's lots come in on brew day.
 */
function LineLink({ recipeId, line }: { recipeId: string; line: RecipeLine }) {
  const link = useChange(send.linkRecipeLine);
  const linkTo = (itemId: string | null) => link.mutate({ recipeId, lineId: line.id, itemId });
  // Adding the catalog's product and linking this line to it, in one press.
  const adopt = useChange(send.item, { onSuccess: (item) => linkTo(item.id) });
  const [searching, setSearching] = useState(false);

  if (line.item) {
    return (
      <span className="row line-matched">
        <span className="chip positive">
          <Check size={14} /> Linked
        </span>
        <button
          type="button"
          className="link-button"
          disabled={link.isPending}
          onClick={() => linkTo(null)}
        >
          Unlink
        </button>
      </span>
    );
  }
  const sug = line.suggestion;
  return (
    <div className="stack line-match">
      {sug?.item ? (
        <div className="row">
          <button
            type="button"
            className={
              sug.item.via === "remembered" ? "button small primary" : "button small suggested"
            }
            disabled={link.isPending}
            onClick={() => linkTo(sug.item?.id ?? null)}
          >
            <Link2 size={14} /> Use {withGrownForm(sug.item.name, sug.item.hopForm)}
          </button>
          <span className="muted line-sku">
            {sug.item.via === "remembered"
              ? "Linked this way before"
              : "Suggested from its name, check it"}
          </span>
        </div>
      ) : sug?.catalogProduct ? (
        <div className="row">
          <button
            type="button"
            className="button small suggested"
            disabled={adopt.isPending}
            onClick={() =>
              adopt.mutate({
                source: "catalog",
                catalogProductId: sug.catalogProduct?.id as string,
              })
            }
          >
            <PackagePlus size={14} /> Add {sug.catalogProduct.name}
          </button>
          <span className="muted line-sku">In the catalog, not stocked here yet</span>
        </div>
      ) : null}
      <div className="row">
        <button
          type="button"
          className="button small"
          aria-label={`Link ${line.name} to an item`}
          disabled={link.isPending}
          onClick={() => setSearching(true)}
        >
          <Search size={14} /> Link to an item
        </button>
      </div>
      <ErrorNote error={link.error ?? adopt.error} />
      {searching ? (
        <LinkPicker
          open
          onClose={() => setSearching(false)}
          line={line}
          onItem={(item) => {
            setSearching(false);
            linkTo(item.id);
          }}
        />
      ) : null}
    </div>
  );
}
function HistorySection({ recipe: r }: { recipe: RecipeDetail }) {
  if (r.history.length === 0) return null;
  return (
    <Section
      id="recipe-history"
      title="History"
      aside={`${r.history.length} ${plural("change", String(r.history.length))}`}
    >
      <ul className="recipe-history">
        {r.history.map((h) => (
          <li key={`${h.at}-${h.summary}`}>
            <span className="faint num">{new Date(h.at).toLocaleString()}</span>
            <span>{h.summary}</span>
            {h.changedBy ? <span className="muted">{h.changedBy}</span> : null}
          </li>
        ))}
      </ul>
    </Section>
  );
}
/** Changes to this recipe left in the builder and not saved over it yet (Tyler, 2026-10-01). */
function WaitingChanges({ recipe: r }: { recipe: RecipeDetail }) {
  const { data: drafts = [] } = useRecipeDrafts();
  const waiting = drafts.find((d) => d.recipe?.id === r.id);
  if (!waiting) return null;
  return (
    <p className="notice recipe-waiting">
      <FileClock size={16} /> Unsaved changes from {showDateTime(waiting.savedAt)} are waiting in
      the builder.{" "}
      <Link to="/recipes/$recipeId/edit" params={{ recipeId: r.id }}>
        Pick them up
      </Link>
    </p>
  );
}
function BrewingSection({ recipe: r }: { recipe: RecipeDetail }) {
  const facts = brewingFacts(r);
  return (
    <Section id="recipe-brewing" title="Brewing" aside="The recipe's own settings">
      <div className="spec-grid">
        {facts.map(([label, value]) => (
          <div key={label} className="spec">
            <div className="label">{label}</div>
            <div className="value num">{value}</div>
          </div>
        ))}
      </div>
      {r.mashSteps.length ? (
        <>
          <h3 className="recipe-subhead">Mash</h3>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Step</th>
                  <th className="num">Temperature</th>
                  <th className="num">Time</th>
                </tr>
              </thead>
              <tbody>
                {r.mashSteps.map((step) => (
                  <tr key={`${step.name}-${step.tempC}-${step.minutes}`}>
                    <td>{step.name}</td>
                    <td className="num">{showFahrenheit(step.tempC)}</td>
                    <td className="num">{step.minutes} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </Section>
  );
}
function ArchiveDialog({
  recipe,
  open,
  onClose,
}: {
  recipe: RecipeDetail;
  open: boolean;
  onClose: () => void;
}) {
  const notify = useNotify();
  const navigate = useNavigate();
  const archive = useChange((_: undefined, key) => send.archiveRecipe(recipe.id, key), {
    onSuccess: () => {
      notify(`${recipe.name} put away`);
      onClose();
      navigate({ to: "/recipes" });
    },
  });
  return (
    <Dialog open={open} onClose={onClose} title="Put this recipe away?">
      <p>{recipe.name} leaves the recipe list. Nothing in inventory changes.</p>
      <ErrorNote error={archive.error} />
      <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="button" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="button primary"
          disabled={archive.isPending}
          onClick={() => archive.mutate(undefined)}
        >
          Put away
        </button>
      </div>
    </Dialog>
  );
}
